import SearchChdTvQueries from './search-chdtv.queries';
import {
  ChdTvIndexes, ChdTvVideos, Episode, EventVideo, Video,
} from './search-chdtv.utilities';
import { SlugPrefix } from '../../models/pages/index';
import { supportedLocales } from '../config.services';
import { formatDateShort, formatDateYear, getTimeEpoch } from '../date.services';

function onlyUnique(value: any, index: any, self: string | any[]) {
  return self.indexOf(value) === index;
}

function parseVideoTags(videoItem:any) {
  return videoItem.videoTags?.videoTags.split(', ') || [];
}

function reduceCategories(acc: any[], current: any) {
  const arraySubCategory = current.name.split('/');
  const transformedCategory = [];
  transformedCategory.push(arraySubCategory.splice(0, 2).join('/'));
  while (arraySubCategory.length) {
    const parentCategory:any = transformedCategory[transformedCategory.length - 1];
    transformedCategory.push(`${parentCategory}/${arraySubCategory.shift()}`);
  }
  return acc.concat(transformedCategory);
}

function mapEpisodeToSearchRecord(data: any) {
  return (data.allContentfulShow.nodes
    .reduce((acc: Episode[], show: any) => {
      if (show.episodes) {
        const defaultLocale = supportedLocales.find((locale) => locale.isDefault)!.id;
        acc.push(...show.episodes
          .filter((episode: any) => episode.visible)
          .map((episode:any) => <Episode>{
            objectID: episode.id,
            id: episode.id,
            internal: episode.internal,
            videoTitle: episode.title,
            videoDescription: episode.description?.description,
            publishedDate: formatDateShort(episode.publishDate)!,
            publishedDateTimestamp: Math.round(getTimeEpoch(episode.publishDate) / 1000),
            locale: episode.node_locale,
            videoYear: formatDateYear(episode.publishDate)?.toString() ?? '',
            videoSlug: episode.node_locale === defaultLocale ? `${SlugPrefix.ChdTvShows}${show.slug}/${episode.slug}/` : `/${episode.node_locale}${SlugPrefix.ChdTvShows}${show.slug}/${episode.slug}/`,
            episodeNumber: episode.episodeNumber,
            videoThumbnail: episode.thumbnailMediaStandard?.gatsbyImageData
            || show.thumbnailMediaStandard?.gatsbyImageData
            || null,
            showTitle: show.title,
            showDescription: show.description?.description,
            contentType: ChdTvVideos.Episode.contentType,
            categories: episode.categories
              ?.reduce(reduceCategories, []).filter(onlyUnique) || [],
            tags: parseVideoTags(episode),

          }));
      }
      return acc;
    }, []));
}
function mapEventToSearchRecord(data: any) {
  return (data.allContentfulEvent.nodes
    .reduce((acc: EventVideo[], event: any) => {
      if (event.eventVideos) {
        const defaultLocale = supportedLocales.find((locale) => locale.isDefault)!.id;
        acc.push(...event.eventVideos
          .filter((eventVideo: any) => eventVideo.visible)
          .map((eventVideo:any) => <EventVideo>{
            objectID: eventVideo.id,
            id: eventVideo.id,
            internal: eventVideo.internal,
            videoTitle: eventVideo.title,
            videoDescription: eventVideo.description?.description,
            publishedDate: formatDateShort(eventVideo.publishDate),
            publishedDateTimestamp: Math.round(getTimeEpoch(eventVideo.publishDate) / 1000),
            locale: eventVideo.node_locale,
            videoYear: formatDateYear(eventVideo.publishDate)?.toString() ?? '',
            videoSlug: eventVideo.node_locale === defaultLocale
              ? `${SlugPrefix.ChdTvEvents}${event.slug}/${eventVideo.slug}/`
              : `/${eventVideo.node_locale}${SlugPrefix.ChdTvEvents}${event.slug}/${eventVideo.slug}/`,
            videoThumbnail: eventVideo.thumbnailMediaStandard
              ?.gatsbyImageData || event.thumbnailMediaStandard?.gatsbyImageData || null,
            eventTitle: event.title,
            eventDescription: event.description?.description,
            contentType: ChdTvVideos.EventVideo.contentType,
            categories: eventVideo.categories
              ?.reduce(reduceCategories, []).filter(onlyUnique) || [],
            tags: parseVideoTags(eventVideo),
          }));
      }
      return acc;
    }, [])
  );
}

function mapVideoToSearchRecord(data: any) {
  const defaultLocale = supportedLocales.find((locale) => locale.isDefault)!.id;
  return (data.allContentfulVideo.nodes
    .map((video:any) => <Video>{
      objectID: video.id,
      id: video.id,
      internal: video.internal,
      videoTitle: video.title,
      videoDescription: video.description?.description,
      publishedDate: formatDateShort(video.publishDate),
      publishedDateTimestamp: Math.round(getTimeEpoch(video.publishDate) / 1000),
      locale: video.node_locale,
      videoYear: formatDateYear(video.publishDate)?.toString() ?? '',
      videoSlug: video.node_locale === defaultLocale
        ? `${SlugPrefix.ChdTvVideos}${video.slug}/`
        : `/${video.node_locale}${SlugPrefix.ChdTvVideos}${video.slug}/`,
      videoThumbnail: video.thumbnailMediaStandard?.gatsbyImageData || null,
      contentType: ChdTvVideos.Video.contentType,
      categories: video.categories?.reduce(reduceCategories, []).filter(onlyUnique) || [],
      tags: parseVideoTags(video),
    })
  );
}
const chdTvQueries = <any>[];

const SearchChdTvSettings = {
  searchableAttributes: [
    'videoTitle',
    'videoDescription',
    'tags',
    'showTitle',
    'showDescription',
    'eventTitle',
    'eventDescription',
  ],
  attributesForFaceting: [
    'id',
    'locale',
    'videoYear',
    'contentType',
    'categories',
    'showTitle',
  ],
};

const matchFields = [
  'internal.contentDigest',
];

const episodeLocaleQueryObject = {
  query: SearchChdTvQueries.showsQuery,
  transformer: ({ data }: any) => mapEpisodeToSearchRecord(data),
};

const eventLocaleQueryObject = {
  query: SearchChdTvQueries.eventsQuery,
  transformer: ({ data }: any) => mapEventToSearchRecord(data),
};
const videoLocaleQueryObject = {
  query: SearchChdTvQueries.videosQuery,
  transformer: ({ data }: any) => mapVideoToSearchRecord(data),
};
chdTvQueries.push(episodeLocaleQueryObject, eventLocaleQueryObject, videoLocaleQueryObject);

const queries = { chdTvQueries };

const CHDTV_INDEX_STORED_NAME = 'CHDTV_SEARCH_INDEX';
const CHDTV_QUERY_STORED_NAME = 'CHDTV_SEARCH_QUERY';

export {
  ChdTvIndexes,
  CHDTV_INDEX_STORED_NAME,
  CHDTV_QUERY_STORED_NAME,
  ChdTvVideos,
  matchFields,
  queries,
  SearchChdTvSettings,
};
